import { type SocketMessageType } from '@/constants/socketMessages';
import { type ValueOf } from '@/types';
import type { InjectionKey, Ref } from 'vue';
import { type WebSocket as MockWebSocket } from 'mock-socket';
import {
	type Project,
	type Booking,
	type User,
	type RoleProject,
	type Role,
	type EliChatThread,
} from '@/generated/graphql';

import { type ISocketEliChatCurrentActionPayload } from '@/features/Eli/components/EliChat/types';

export const SOCKET_PROVIDE_KEY = Symbol('socket') as InjectionKey<
	Ref<WebSocket | MockWebSocket | undefined> | undefined
>;

/**
 * A base message type that declares the common fields.
 *
 * Type parameters:
 * - Type: The name of the event
 * - Data: The extra data payload of the event
 */
export type BaseSocketMessage<Type extends string, Data> = {
	/**
	 * Payload sent by the BE. Different for all messages.
	 */
	data: Data;
	siteName: string;
	timeoutInSeconds: number;
	type: Type;
	/**
	 * Array of user tokens for who this notification is relevant to.
	 *
	 * We shouldn't need to check this in the FE because the BE should only
	 * send the websocket
	 * message to the correct users.
	 */
	users: string[];
};

/**
 * A small helper type for defining time tracker socket message payloads.
 */
type SocketTimeTrackerPayload = {
	task_id: number;
	timer_seconds: number;
	tracker_id: number;
	without_task_time_entry_id: null;
};

/**
 * Time "entry" socket message payloads. I'm not sure what the difference
 * between a "time tracker" and a "time entry" is, but they have different
 * payloads.
 */
export type SocketTimeEntryPayload = {
	/** YYYY-MM-DD */
	date: string;
	event_id: number;
	event_type: string;
	project_id: number;
	time_entry_id: number | string;
};

export type SocketTaskPayload = {
	assigned_to: Array<number | string>;
	assigned_roles?: Array<number | string>;
	bill_id: number | string;
	company_id: number | string;
	/** YYYY-MM-DD HH:mm:ss */
	datetime_due: string;
	duration_planned: number;
	event_id: number | string;
	event_name: string;
	invoice_id: number | string;
	is_completed: boolean;
	meter_id: number | string;
	order_id: number | string;
	owner_id: number | string;
	person_id: number | string;
	previous_project_id: number | null;
	priority: number | string;
	project_id: number;
	project_phase_id: number;
	property_id: number;
	purchase_order_id: number | string;
	quote_id: number | string;
	real_estate_id: number | null;
	rent_order_id: number | string;
	/** YYYY-MM-DD HH:mm:ss */
	start_datetime: string;
	parent_id: number | null;
};

export type SocketTrialProgressChangedPayload = {
	percent: number;
};

export type SocketPhasePayload = {
	id: number;
	project_id: number;
	type: string;
	title: string;
	start_date?: string | null;
	end_date?: string | null;
	ordering: number;
	quote_line_id?: number | null;
};

export type SocketEventPayload = {
	changer_id: number;
	end_date: string;
	event_id: string;
	event_name: string;
	is_related_to_task: boolean;
	multipleDays: boolean;
	previous_project_id: null;
	project_id: number;
	project_phase_id: number;
	/** YYYY-MM-DD */
	start_date: string;
	/** YYYY-MM-DD HH:mm:ss */
	start_datetime: string;
	parent_id: number | null;
};

export type SocketEliChatThreadPayload = {
	threadId: EliChatThread['threadId'];
	title: EliChatThread['title'];
	user: {
		uuid: EliChatThread['user']['id'];
	};
};

/**
 * All possible messages with their payloads.
 *
 * To add a TypeScript type for a new message:
 * 1. Add a new message type to the `MessageType` constant
 * 2. Check the message payload in code or log it somehow
 * 3. Add a new message type as well as its payload type here
 */
export type SocketMessage = {
	// Notifications
	[SocketMessageType.NOTIFICATION_CREATED]: { objectId: number };
	[SocketMessageType.MARK_NOTIFICATION_READ]: { notificationId: string };
	[SocketMessageType.MARK_NOTIFICATION_UNREAD]: { notificationId: string };
	[SocketMessageType.MARK_ALL_NOTIFICATIONS_READ]: never[];

	// Time entries
	[SocketMessageType.TIME_TRACKER_STARTED]: SocketTimeTrackerPayload;
	[SocketMessageType.TIME_TRACKER_PAUSED]: SocketTimeTrackerPayload;
	[SocketMessageType.TIME_TRACKER_COMPLETED]: SocketTimeTrackerPayload;
	[SocketMessageType.TIME_ENTRY_CREATED]: SocketTimeEntryPayload;
	[SocketMessageType.TIME_ENTRY_UPDATED]: SocketTimeEntryPayload;
	[SocketMessageType.TIME_ENTRY_DELETED]: SocketTimeEntryPayload;

	// Tasks
	[SocketMessageType.TASK_CREATED]: SocketTaskPayload;
	[SocketMessageType.TASK_UPDATED]: SocketTaskPayload;
	[SocketMessageType.TASK_SET_DONE]: SocketTaskPayload;

	[SocketMessageType.TRIAL_PROGRESS_CHANGED]: SocketTrialProgressChangedPayload;
	[SocketMessageType.TRIAL_PROGRESS_SITE_READY]: never;

	// Events
	[SocketMessageType.EVENT_CREATED]: SocketEventPayload;
	[SocketMessageType.EVENT_UPDATED]: SocketEventPayload;
	[SocketMessageType.EVENT_DELETED]: SocketEventPayload;

	// Eli
	[SocketMessageType.ELI_CHAT_THREAD_CREATED]: SocketEliChatThreadPayload;
	[SocketMessageType.ELI_CHAT_THREAD_UPDATED]: SocketEliChatThreadPayload;
	[SocketMessageType.ELI_CHAT_CURRENT_ACTION]: ISocketEliChatCurrentActionPayload;

	// Bookings
	[SocketMessageType.BOOKING_CREATED]: Booking;
	[SocketMessageType.BOOKING_UPDATED]: Booking;
	[SocketMessageType.BOOKINGS_DELETED]: Record<'ids', Array<Booking['id']>>;

	// Project
	[SocketMessageType.PROJECT_MEMBERS_CHANGED]: {
		projectId: Project['id'];
		addedUserIds: Array<User['id']>;
		removedUserIds: Array<User['id']>;
		addedRoleProjects: Array<{
			id: RoleProject['id'];
			role: {
				id: Role['id'];
			};
		}>;
		removedRoleProjects: Array<{
			id: RoleProject['id'];
			role: {
				id: Role['id'];
			};
		}>;
	};

	[SocketMessageType.PROJECT_TIMEFRAME_CHANGED]: {
		project_id: number;
	};
	[SocketMessageType.PROJECT_CHANGED]: {
		project_id: number;
	};
	[SocketMessageType.PROJECT_CACHE_CHANGED]: Array<Project['id']>;
	[SocketMessageType.PROJECT_HEALTH_SCORE_CHANGED]: {
		project_id: number;
		health_score: string;
		reference_id: string;
	};

	// Project phases
	[SocketMessageType.NEW_PHASE_ADDED]: SocketPhasePayload;
	[SocketMessageType.PHASE_CHANGED]: SocketPhasePayload;
	[SocketMessageType.PHASE_DELETED]: SocketPhasePayload;

	// Time Off
	[SocketMessageType.TIME_OFF_UPDATED]: {
		userIds: Array<User['id']>;
	};
	[SocketMessageType.TIME_OFF_DELETED]: {
		userIds: Array<User['id']>;
	};
};

/**
 * Websocket message listeners. An object where the key is the message name and
 * the value is a function which accepts the payload.
 */
export type SocketListeners = {
	[P in keyof SocketMessage]?: (
		message: BaseSocketMessage<P, SocketMessage[P]>
	) => void | Promise<void>;
};

export type RegisteredSocketListeners = Record<
	string,
	[
		{
			uuid: string;
			cb: (message: AllSocketMessages) => void | Promise<void>;
		},
	]
>;

/**
 * A union of all socket message payloads.
 */
export type AllSocketMessages = BaseSocketMessage<
	keyof SocketMessage,
	ValueOf<SocketMessage>
>;
